import {useMemo} from 'react'
import {toast} from 'sonner'
import dayjs from '../utils/localDayjs'
import {useListPeriod, useUIActions} from '../store'
import {expandInclusiveDateRange} from '../utils/dates'
import {getTranslation} from '../utils/getTranslation'
import {MAX_LIST_PERIOD_DAYS} from '../constants/constants'

/**
 * Drives the list "period" (date-range) picker (activities + notices tabs). The picker works in
 * expanded day arrays; the store keeps a compact {from, to}. This hook bridges
 * the two and enforces the soft span cap (clamp + toast) before writing the
 * range. The API re-validates the cap defensively.
 */
export const useListPeriodPicker = () => {
  const period = useListPeriod()
  const {setListPeriod} = useUIActions()

  // Expanded days feed the range calendar's initial selection.
  const value = useMemo(
    () => (period ? expandInclusiveDateRange(period.from, period.to) : []),
    [period],
  )

  // The calendar emits the fully-expanded list of every day in the span; we keep
  // only the endpoints, clamping the span to the cap.
  const onPickRange = (dates: string[]) => {
    if (dates.length === 0) {
      setListPeriod(null)
      return
    }
    const from = dates[0]
    let to = dates[dates.length - 1]
    if (dates.length > MAX_LIST_PERIOD_DAYS) {
      to = dayjs(from)
        .add(MAX_LIST_PERIOD_DAYS - 1, 'day')
        .format('YYYY-MM-DD')
      toast.warning(
        getTranslation('activityList:periodCapped', [
          String(MAX_LIST_PERIOD_DAYS),
        ]),
      )
    }
    setListPeriod({from, to})
  }

  return {value, onPickRange}
}
